import { useMemo, useState } from 'react';
import { Alert, Button, Dropdown, Input, Modal, Space, Typography, message } from 'antd';
import { ClearOutlined, DownOutlined, DownloadOutlined } from '@ant-design/icons';
import { ERPTable } from '@/components/DataTable';
import { useWarehouses } from '@/hooks/useWarehouses';
import { useStores } from '@/hooks/useStoreSettings';
import { useCategoryOptions } from '@/hooks/useCategories';
import { useStockLookupList } from '@/hooks/useStockLookup';
import {
  stockLookupService,
  type StockLookupMode,
  type StockLookupProductRow,
} from '@/services/stockLookup.service';
import {
  buildComboColumns,
  buildProductColumns,
  lotColumns,
  serialColumns,
} from './parts/columns';
import { buildAmisAsOfNote } from './parts/amisAsOf';
import { SearchableFilterSelect } from './parts/SearchableFilterSelect';
import { WarehouseBreakdownModal } from './WarehouseBreakdownModal';
import { ReservedOrdersModal } from './ReservedOrdersModal';
import { waitForExportJobDownload } from '@/components/Excel/exportJobDownload';

/** antd 5 không export `Text` ở top-level — phải lấy qua `Typography.Text`. */
const { Text } = Typography;

const MODE_LABELS: Record<StockLookupMode, string> = {
  products: 'Tra cứu hàng tồn kho',
  lots: 'Tra cứu tồn kho theo lô',
  serials: 'Tra cứu tồn kho theo mã quy cách',
  combos: 'Tra cứu tồn kho combo hàng hoá',
};

/** TCR-1985 — chế độ hàng hoá/combo gộp toàn kho nên mỗi dòng là MỘT mặt hàng; chế độ
 *  lô/mã quy cách là một dòng cho mỗi (bản ghi × kho). Ghi "dòng" chung cho cả hai khiến QA
 *  so "75 dòng" ở đây với "198 dòng" (biến thể × kho) ở màn Tồn kho và tưởng lệch dữ liệu. */
const TOTAL_UNITS: Record<StockLookupMode, string> = {
  products: 'mặt hàng',
  lots: 'dòng',
  serials: 'dòng',
  combos: 'mặt hàng',
};

/** tableKey riêng cho từng chế độ — bốn bảng có bốn bộ cột khác nhau, dùng chung key
 *  sẽ làm layout cột người dùng lưu ở chế độ này đè lên chế độ kia. */
const TABLE_KEYS: Record<StockLookupMode, string> = {
  products: 'stock-lookup-products',
  lots: 'stock-lookup-lots',
  serials: 'stock-lookup-serials',
  combos: 'stock-lookup-combos',
};

/**
 * `category_id` được cả 4 route chấp nhận, nhưng service nội bộ của 2 chế độ lô/mã quy
 * cách KHÔNG đọc field này (Task 7, deferred) — lọc theo Loại hàng hoá ở 2 chế độ đó là
 * IM LẶNG không có tác dụng. Ẩn hẳn control filter ở 2 chế độ này thay vì disable: một
 * ô disable vẫn còn hiện giá trị đã chọn từ chế độ khác, dễ khiến người dùng tưởng nó
 * đang lọc mà chỉ là "tạm khoá". Ẩn hẳn tránh gây hiểu lầm đó.
 */
const CATEGORY_FILTER_MODES = new Set<StockLookupMode>(['products', 'combos']);

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Popup con đang mở, nếu có. */
type OpenPopup = 'breakdown' | 'reserved' | null;

/**
 * TCR-1954 — popup Tra cứu tồn kho mở từ icon trên header.
 *
 * State cục bộ, CỐ Ý không sync URL (lệch khỏi rule 3 của CLAUDE.md, đã được duyệt):
 * popup này mở đè lên bất kỳ trang nào, nên đẩy mode/page/search/warehouse_id vào query
 * string sẽ làm bẩn URL của trang đang đứng và đá nhau với bộ lọc của chính trang đó
 * (vd mở popup khi đang ở `/orders?page=3`).
 */
export function StockLookupModal({ open, onClose }: Props) {
  const [mode, setMode] = useState<StockLookupMode>('products');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  // Chữ đang gõ trong ô tìm kiếm — tách khỏi `search` (chỉ áp dụng khi Enter/bấm kính lúp)
  // để nút "Xoá bộ lọc" xoá được cả chữ đang hiện trong ô (TCR-1991).
  const [searchInput, setSearchInput] = useState('');
  // TCR-2572 — chi nhánh đứng trước kho: tên kho trùng nhau giữa các chi nhánh.
  const [storeId, setStoreId] = useState<string | undefined>();
  const [warehouseId, setWarehouseId] = useState<string | undefined>();
  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [selectedVariant, setSelectedVariant] =
    useState<StockLookupProductRow | null>(null);
  const [openPopup, setOpenPopup] = useState<OpenPopup>(null);
  const [exporting, setExporting] = useState(false);

  const showCategoryFilter = CATEGORY_FILTER_MODES.has(mode);
  const hasActiveFilter = Boolean(
    search || searchInput || storeId || warehouseId || (showCategoryFilter && categoryId),
  );

  /** TCR-1991 — xoá hết tìm kiếm + lọc bằng một thao tác. */
  function clearFilters() {
    setSearch('');
    setSearchInput('');
    setStoreId(undefined);
    setWarehouseId(undefined);
    setCategoryId(undefined);
    setPage(1);
  }

  const query = useMemo(
    () => ({
      page,
      page_size: pageSize,
      search,
      store_id: storeId,
      warehouse_id: warehouseId,
      // Chỉ gửi category_id ở 2 chế độ thực sự lọc theo nó — xem CATEGORY_FILTER_MODES.
      category_id: showCategoryFilter ? categoryId : undefined,
    }),
    [page, pageSize, search, storeId, warehouseId, categoryId, showCategoryFilter],
  );

  // `enabled: open` — component này mount ở MỌI trang (nằm trong Header), không gate thì
  // mỗi lần app load là bắn một request tra cứu tồn kho vô ích.
  const { data, isLoading, isError, refetch } = useStockLookupList(mode, query, {
    enabled: open,
  });
  const { data: warehouses } = useWarehouses({ page_size: 200 }, { enabled: open });
  const { data: stores } = useStores({ enabled: open });
  const { options: categoryList } = useCategoryOptions({ enabled: open });

  const storeLabels = useMemo(
    () => new Map((stores ?? []).map((st) => [st._id, `${st.code} - ${st.name}`])),
    [stores],
  );

  // Chỉ những chi nhánh có kho trong danh sách kho người dùng xem được — chi nhánh không có
  // kho nào thì chọn vào cũng chỉ ra bảng trống.
  const storeOptions = useMemo(() => {
    const withWarehouse = new Set((warehouses?.data ?? []).map((w) => w.store_id));
    return [
      { value: '', label: 'Tất cả chi nhánh' },
      ...(stores ?? [])
        .filter((st) => withWarehouse.has(st._id))
        .map((st) => ({ value: st._id, label: `${st.code} - ${st.name}` })),
    ];
  }, [stores, warehouses]);

  /** Kho → chi nhánh, để chọn kho trước thì tự điền chi nhánh. */
  const warehouseStore = useMemo(
    () =>
      new Map(
        (warehouses?.data ?? []).map((w) => [
          String(w._id ?? w.finished_good_warehouse_id ?? ''),
          w.store_id ?? undefined,
        ]),
      ),
    [warehouses],
  );

  const warehouseOptions = useMemo(
    () => [
      { value: '', label: 'Tất cả kho' },
      ...(warehouses?.data ?? [])
        .filter((w) => !storeId || w.store_id === storeId)
        .map((w) => ({
          value: String(w._id ?? w.finished_good_warehouse_id ?? ''),
          label: `${w.code} - ${w.name}`,
          // Kho trùng tên giữa các chi nhánh — dòng phụ cho biết kho thuộc chi nhánh nào.
          description:
            (w.store_id ? storeLabels.get(w.store_id) : undefined) ?? w.store_name ?? undefined,
        })),
    ],
    [warehouses, storeId, storeLabels],
  );

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'Tất cả loại hàng hoá' },
      ...categoryList,
    ],
    [categoryList],
  );

  function changeMode(next: StockLookupMode) {
    setMode(next);
    // Giữ search/kho (người dùng đang tra cùng một mặt hàng ở góc nhìn khác), reset trang.
    setPage(1);
  }

  const productColumns = useMemo(
    () =>
      buildProductColumns({
        onQuantityClick: (row) => {
          setSelectedVariant(row);
          setOpenPopup('breakdown');
        },
        onReservedClick: (row) => {
          setSelectedVariant(row);
          setOpenPopup('reserved');
        },
        preferredCategoryId: categoryId,
      }),
    [categoryId],
  );
  const comboColumns = useMemo(() => buildComboColumns(categoryId), [categoryId]);

  // TCR-2523 — chú thích thời điểm cập nhật tồn AMIS. CHỈ hiện ở chế độ hàng hoá:
  // đó là chế độ duy nhất có cột "Tồn AMIS" để đối chiếu. Ở chế độ lô/mã quy cách/combo
  // mốc thời gian này không gắn với bất kỳ con số nào đang hiện → hiện ra chỉ gây nhiễu.
  const amisNote = useMemo(
    () => (mode === 'products' ? buildAmisAsOfNote(data?.amis_as_of) : null),
    [mode, data?.amis_as_of],
  );

  const columns =
    mode === 'products'
      ? productColumns
      : mode === 'lots'
        ? lotColumns
        : mode === 'serials'
          ? serialColumns
          : comboColumns;

  // TCR-1986 — endpoint chỉ TẠO job (worker sinh file bất đồng bộ). Trước đây chỉ báo
  // "đã tạo yêu cầu" rồi thôi nên file không bao giờ về máy dù job đã completed; phải poll
  // job tới khi xong rồi đưa nút Tải xuống, cùng cách các màn export khác.
  async function handleExport() {
    setExporting(true);
    try {
      const job = await stockLookupService.requestExport(mode, query);
      await waitForExportJobDownload(job, {
        notificationKey: `excel-export-stock-lookup-${job.export_id}`,
      });
    } catch {
      message.error('Không tạo được yêu cầu xuất Excel');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        width={1200}
        destroyOnClose
        footer={<Button onClick={onClose}>Đóng</Button>}
        title={
          <Dropdown
            trigger={['click']}
            menu={{
              selectedKeys: [mode],
              items: (Object.keys(MODE_LABELS) as StockLookupMode[]).map((key) => ({
                key,
                label: MODE_LABELS[key],
              })),
              onClick: ({ key }) => changeMode(key as StockLookupMode),
            }}
          >
            <a
              onClick={(e) => e.preventDefault()}
              data-testid="stock-lookup-mode-trigger"
            >
              <Space>
                {MODE_LABELS[mode]}
                <DownOutlined />
              </Space>
            </a>
          </Dropdown>
        }
      >
        <Space style={{ marginBottom: 12 }} wrap>
          <Input.Search
            allowClear
            placeholder="Tìm kiếm hàng hoá"
            style={{ width: 220 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={(value) => {
              setSearch(value);
              setPage(1);
            }}
          />
          <SearchableFilterSelect
            width={200}
            value={storeId ?? ''}
            options={storeOptions}
            data-testid="stock-lookup-store-filter"
            onChange={(value) => {
              const next = value || undefined;
              setStoreId(next);
              // Kho đang chọn không thuộc chi nhánh mới thì bỏ chọn, không để lọc chồng ra rỗng.
              if (next && warehouseId && warehouseStore.get(warehouseId) !== next) {
                setWarehouseId(undefined);
              }
              setPage(1);
            }}
          />
          <SearchableFilterSelect
            width={220}
            value={warehouseId ?? ''}
            options={warehouseOptions}
            data-testid="stock-lookup-warehouse-filter"
            onChange={(value) => {
              setWarehouseId(value || undefined);
              // Chọn kho trước khi chọn chi nhánh → tự điền chi nhánh của kho đó.
              const owner = value ? warehouseStore.get(value) : undefined;
              if (owner && !storeId) setStoreId(owner);
              setPage(1);
            }}
          />
          {showCategoryFilter && (
            <SearchableFilterSelect
              width={200}
              value={categoryId ?? ''}
              options={categoryOptions}
              data-testid="stock-lookup-category-filter"
              onChange={(value) => {
                setCategoryId(value || undefined);
                setPage(1);
              }}
            />
          )}
          <Button
            icon={<ClearOutlined />}
            disabled={!hasActiveFilter}
            onClick={clearFilters}
            data-testid="stock-lookup-clear-filters"
          >
            Xoá bộ lọc
          </Button>
          <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
            Xuất Excel
          </Button>
        </Space>

        {/* TCR-2523 — mốc thời gian cập nhật tồn AMIS, đặt NGAY DƯỚI thanh lọc để
            người dùng đọc cùng lúc với tiêu đề bảng. Quá 60 phút thì chuyển chữ sang
            màu cảnh báo — số liệu lúc đó có thể đã cũ so với tồn ERP. */}
        {amisNote && (
          <div style={{ marginBottom: 12 }} data-testid="stock-lookup-amis-as-of">
            <Text type={amisNote.stale ? 'warning' : undefined}>{amisNote.text}</Text>
          </div>
        )}

        {isError && (
          // Không có khối này thì API hỏng trông y hệt "kho không có hàng" — bảng chỉ hiện
          // "Trống" (QA 23/09 đã báo FAILED đúng vì thế).
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="Không tải được dữ liệu tồn kho. Vui lòng thử lại."
            action={
              <Button size="small" onClick={() => refetch()}>
                Thử lại
              </Button>
            }
          />
        )}

        <ERPTable
          tableKey={TABLE_KEYS[mode]}
          // TCR-1996 — kẻ viền bảng và từng dòng cho dễ dò cột khi dòng xuống nhiều hàng chữ.
          bordered
          rowKey={(row: Record<string, unknown>) =>
            String(row.variant_id ?? row.lot_id ?? row.serial_id)
          }
          loading={isLoading}
          columns={columns as never}
          dataSource={(data?.data ?? []) as never}
          scroll={{ x: 'max-content', y: 420 }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            showTotal: (total) => `${total} ${TOTAL_UNITS[mode]}`,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />

        {/* TCR-2573 — hai popup con PHẢI nằm trong cây của Modal này: antd chỉ tự nâng
            z-index cho Modal lồng trong Modal. Để ngang hàng thì cả hai cùng z-index 1000 và
            ai nằm sau trong <body> thắng — Modal này có destroyOnClose nên mỗi lần mở lại bị
            gắn xuống cuối body, popup con (mở từ lần trước) bị che bên dưới. */}
        <WarehouseBreakdownModal
          open={openPopup === 'breakdown'}
          variant={selectedVariant}
          // Con số vừa bấm ở cột "Số lượng" là tồn của ĐÚNG kho đang lọc; không truyền kho
          // xuống thì popup liệt kê mọi kho và tổng các dòng không khớp con số đó.
          warehouseId={warehouseId}
          storeId={storeId}
          onClose={() => setOpenPopup(null)}
        />
        <ReservedOrdersModal
          open={openPopup === 'reserved'}
          variant={selectedVariant}
          // Truyền kho đang lọc xuống để danh sách đơn khớp đúng bộ lọc người dùng đang xem.
          warehouseId={warehouseId}
          storeId={storeId}
          onClose={() => setOpenPopup(null)}
        />
      </Modal>
    </>
  );
}
